import { useEffect, useLayoutEffect, useRef } from "react";

export function useStickyHeader(measureAndApply: () => void) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const cloneRootRef = useRef<HTMLDivElement | null>(null);
  const cloneInnerRef = useRef<HTMLDivElement | null>(null);
  const cloneCellsRef = useRef<HTMLElement[] | null>(null);
  const isCloneVisibleRef = useRef(false);

  useEffect(() => {
    const scrollEl = scrollRef.current;
    const cloneInner = cloneInnerRef.current;
    if (!scrollEl || !cloneInner) return;

    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          cloneInner.style.transform = `translate3d(${-scrollEl.scrollLeft}px,0,0)`;
          ticking = false;
        });
        ticking = true;
      }
    };
    scrollEl.addEventListener("scroll", onScroll, { passive: true });
    return () => scrollEl.removeEventListener("scroll", onScroll);
  }, []);

  useLayoutEffect(() => {
    const sentinel = sentinelRef.current;
    const cloneRoot = cloneRootRef.current;
    if (!sentinel || !cloneRoot) return;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.intersectionRatio === 0 && entry.boundingClientRect.y < 0) {
          if (!isCloneVisibleRef.current) {
            isCloneVisibleRef.current = true;
            cloneRoot.style.display = "block";
            measureAndApply();
          }
        } else if (isCloneVisibleRef.current) {
          isCloneVisibleRef.current = false;
          cloneRoot.style.display = "none";
        }
      },
      { threshold: [0, 1] },
    );

    io.observe(sentinel);
    return () => io.disconnect();
  }, [measureAndApply]);

  useEffect(() => {
    const onResize = () => measureAndApply();
    window.addEventListener("resize", onResize);
    document.fonts?.ready?.then(measureAndApply).catch(() => {});
    setTimeout(measureAndApply, 0);
    return () => window.removeEventListener("resize", onResize);
  }, [measureAndApply]);

  return {
    scrollRef,
    headerRef,
    sentinelRef,
    cloneRootRef,
    cloneInnerRef,
    cloneCellsRef,
    isCloneVisibleRef,
  };
}
